import { useState } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { errMsg } from "@/lib/api";
import AuthShell from "@/pages/AuthShell";

const fields = [
  { k: "name", label: "Full name", type: "text", required: true },
  { k: "email", label: "Email", type: "email", required: true },
  { k: "phone", label: "Phone (optional)", type: "tel", required: false },
  { k: "password", label: "Password (min 6 characters)", type: "password", required: true },
];

export default function Register() {
  const { user, register } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const ref = (params.get("ref") || "").toUpperCase();
  const [form, setForm] = useState({ invite_code: ref ? "" : params.get("code") || "", referral_code: ref, name: "", email: "", phone: "", password: "" });
  const [useInvite, setUseInvite] = useState(!ref);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  if (user) return <Navigate to={user.role === "admin" ? "/admin" : "/shop"} replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await register(useInvite ? { ...form, referral_code: "" } : form);
      nav("/shop");
    } catch (err) {
      setError(errMsg(err));
      if (!useInvite && err?.response?.status === 400 && /referral/i.test(errMsg(err))) setUseInvite(true);
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell eyebrow={ref ? "Referred by a friend" : "Invite only"} title="Create your client account">
      <form onSubmit={submit} className="space-y-4">
        {!useInvite ? (
          <p className="rounded-md border border-emerald-500/40 bg-emerald-500/10 p-3 text-sm text-emerald-300" data-testid="register-referral-banner">
            A friend referred you. Once Korevex Bio approves your account, you'll get a welcome discount on your first order.
          </p>
        ) : (
        <div className="space-y-2">
          <Label htmlFor="invite_code">Invite code</Label>
          <Input id="invite_code" required value={form.invite_code} placeholder="e.g. K7XM2Q9P"
            onChange={(e) => setForm({ ...form, invite_code: e.target.value.toUpperCase() })}
            className="font-mono-data tracking-widest" data-testid="register-invite-code-input" />
        </div>
        )}
        {fields.map((f) => (
          <div className="space-y-2" key={f.k}>
            <Label htmlFor={f.k}>{f.label}</Label>
            <Input id={f.k} type={f.type} required={f.required} minLength={f.k === "password" ? 6 : undefined}
              value={form[f.k]} onChange={(e) => setForm({ ...form, [f.k]: e.target.value })} data-testid={`register-${f.k}-input`} />
          </div>
        ))}
        <label className="flex items-start gap-3 rounded-md border border-amber-500/30 bg-amber-500/5 p-3 text-xs leading-relaxed text-muted-foreground">
          <input type="checkbox" required checked={!!form.research_ack} onChange={(e) => setForm({ ...form, research_ack: e.target.checked })}
            className="mt-0.5 h-4 w-4 shrink-0 accent-emerald-500" data-testid="register-research-ack-checkbox" />
          <span>I confirm I am 21 or older and that all products are intended for <strong className="text-foreground">research use only</strong>. They are not for human or animal consumption, or for diagnostic or therapeutic use.</span>
        </label>
        {error && <p className="text-sm text-red-400" data-testid="register-error">{error}</p>}
        <Button type="submit" className="w-full" disabled={busy || !form.research_ack} data-testid="register-submit-button">
          {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Create account
        </Button>
        <p className="text-xs text-muted-foreground">Some invites need a quick approval from Korevex Bio before you can order.</p>
      </form>
      <p className="mt-6 text-center text-sm text-muted-foreground">
        Already have an account? <Link to="/login" className="text-emerald-400 hover:underline" data-testid="register-to-login-link">Sign in</Link>
      </p>
    </AuthShell>
  );
}
